import { Link } from "react-router-dom";
import { useApi } from "@/lib/useApi";
import { Badge, PageTitle, card, linkCls } from "@/components/admin/ui";
import { money } from "@/lib/format";

const tone = (s) => ({ paid: "green", pending: "yellow", refunded: "blue", failed: "red", expired: "gray" }[s] || "gray");

export default function AdminSales() {
  const { data } = useApi("/admin/sales");
  if (!data) return <p className="text-[14px] text-[#6B7280]">Loading…</p>;
  const totals = Object.entries(data.totals);
  const currency = totals[0]?.[0] || "usd";

  return (
    <div data-testid="admin-sales-page">
      <PageTitle title="Sales">{data.mode === "test" && <Badge tone="yellow">Stripe test mode</Badge>}</PageTitle>
      <div className="mb-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className={`${card} p-4`}>
          <p className="text-[12px] text-[#6B7280]">Passes sold</p>
          <p className="text-[24px] font-semibold" data-testid="sales-paid-count">{data.paid_count}</p>
        </div>
        <div className={`${card} p-4`}>
          <p className="text-[12px] text-[#6B7280]">Revenue</p>
          <p className="text-[24px] font-semibold" data-testid="sales-revenue">{totals.length ? totals.map(([c, a]) => money(a, c)).join(" · ") : money(0, "usd")}</p>
        </div>
        <div className={`${card} p-4`}>
          <p className="text-[12px] text-[#6B7280]">{data.is_admin ? `Dancerfloor fee (${data.platform_fee_pct}%)` : `Your earnings (after ${data.platform_fee_pct}%)`}</p>
          <p className="text-[24px] font-semibold" data-testid="sales-earnings">{money(data.is_admin ? data.platform_fee : data.earnings, currency)}</p>
        </div>
      </div>

      <div className={`${card} mb-6 overflow-hidden`} data-testid="sales-by-video">
        <p className="border-b border-[#E5E7EB] bg-[#F9FAFB] px-4 py-2.5 text-[12px] font-medium text-[#6B7280]">Where the sales came from</p>
        <div className="overflow-x-auto">
        <table className="w-full min-w-[480px] text-[13px]">
          <tbody className="divide-y divide-[#E5E7EB]">
            {data.by_source.map((r) => (
              <tr key={r.source} data-testid="sales-source-row">
                <td className="px-4 py-2.5">
                  {r.video_id ? (
                    <Link to={`/watch/${r.video_id}`} target="_blank" className={linkCls}>{r.source}</Link>
                  ) : (
                    <span className="text-[#374151]">{r.source}</span>
                  )}
                </td>
                <td className="px-4 py-2.5 text-right text-[#6B7280]">{r.count} pass{r.count === 1 ? "" : "es"}</td>
                <td className="px-4 py-2.5 text-right font-medium">{money(r.gross, r.currency)}</td>
              </tr>
            ))}
            {data.by_source.length === 0 && <tr><td className="px-4 py-6 text-center text-[#6B7280]">No paid sales yet.</td></tr>}
          </tbody>
        </table>
        </div>
      </div>

      <div className={`${card} overflow-hidden`} data-testid="sales-table">
        <div className="overflow-x-auto">
        <table className="w-full min-w-[920px] text-[13px]">
          <thead className="bg-[#F9FAFB] text-left text-[12px] text-[#6B7280]">
            <tr>
              <th className="px-4 py-2.5 font-medium">Date</th>
              <th className="px-4 py-2.5 font-medium">Festival</th>
              <th className="px-4 py-2.5 font-medium">Pass</th>
              <th className="px-4 py-2.5 font-medium">Bought from</th>
              <th className="px-4 py-2.5 font-medium">Buyer</th>
              <th className="px-4 py-2.5 font-medium text-right">Amount</th>
              <th className="px-4 py-2.5 font-medium text-right">{data.is_admin ? "Fee" : "You earned"}</th>
              <th className="px-4 py-2.5 font-medium">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#E5E7EB]">
            {data.sales.map((s) => (
              <tr key={s.session_id} data-testid="sales-row">
                <td className="px-4 py-2.5 whitespace-nowrap text-[#6B7280]">{new Date(s.created_at).toLocaleString()}</td>
                <td className="px-4 py-2.5 font-medium">{s.festival_name}</td>
                <td className="px-4 py-2.5">{s.tier_name}</td>
                <td className="px-4 py-2.5 whitespace-nowrap text-[#6B7280]" data-testid="sales-source">
                  {s.video_id ? (
                    <Link to={`/watch/${s.video_id}`} target="_blank" className={`${linkCls} block max-w-[200px] truncate`}>{s.video_title || "Video"}</Link>
                  ) : (
                    "Festival page"
                  )}
                </td>
                <td className="px-4 py-2.5 whitespace-nowrap text-[#6B7280]">
                  {s.buyer_email ? (
                    <Link to={`/admin/customers/${encodeURIComponent(s.buyer_email)}`} className={linkCls}>{s.buyer_email}</Link>
                  ) : (
                    "—"
                  )}
                </td>
                <td className="px-4 py-2.5 text-right font-medium">{money(s.amount_total, s.currency)}</td>
                <td className="px-4 py-2.5 text-right">{money(data.is_admin ? s.platform_fee : s.organizer_earnings, s.currency)}</td>
                <td className="px-4 py-2.5"><Badge tone={tone(s.payment_status)}>{s.payment_status}</Badge></td>
              </tr>
            ))}
            {data.sales.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-10 text-center text-[#6B7280]">No purchases yet. Sales appear here as soon as someone buys a pass.</td></tr>
            )}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
}
